/**
 * Печатные формы отчётности: X-отчёт, Z-отчёт, Акт реализации.
 *
 * Всё по канону чека 2.64: числа чистые, валюта — одной строкой
 * «ВСЕ СУММЫ В ТЕНГЕ», тяжёлая линия «═» перед итогами, ширина
 * ленты параметром. Данные приходят готовым агрегатом ShiftReport —
 * здесь только раскладка, ни одного вычисления по заказам.
 *
 * Акт реализации — двумя блоками по цехам, как iiko делит по
 * складам: повару своя правда, бармену своя. Внутри блока —
 * по категориям меню, блюда по алфавиту.
 */
import type { ShiftReport } from '@dastarhan/contracts';
import { formatMoney } from './money';

/*
 * Числа на лентах: целые тенге — как привыкли; но если в сумме живут
 * тиыны (процентные скидки их рождают), печатаем с копейками — иначе
 * строки, округлённые порознь, не сходятся в столбик на 1 тенге,
 * и бухгалтер Дыма ловит нас на «недостаче», которой нет
 */
const N = (v: number) => {
  const whole = formatMoney(v - (v % 100)).replace(' ₸', '');
  return v % 100 === 0
    ? whole
    : `${whole},${String(v % 100).padStart(2, '0')}`;
};

function padLR(l: string, r: string, width: number): string {
  const free = width - r.length - 1;
  const left = l.length > free ? l.slice(0, Math.max(0, free - 1)) + '…' : l;
  return left + ' '.repeat(Math.max(1, width - left.length - r.length)) + r;
}

function center(t: string, width: number): string {
  const pad = Math.max(0, Math.floor((width - t.length) / 2));
  return ' '.repeat(pad) + t;
}

const dt = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('ru-RU') + ' '
    + d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
};
const tm = (iso: string) =>
  new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

const qtyStr = (q: number) =>
  Number.isInteger(q) ? String(q) : q.toFixed(3).replace(/\.?0+$/, '');

function checksWord(n: number): string {
  const d = n % 10, h = n % 100;
  if (h >= 11 && h <= 14) return 'чеков';
  if (d === 1) return 'чек';
  if (d >= 2 && d <= 4) return 'чека';
  return 'чеков';
}

const STATION_RU: Record<'KITCHEN' | 'BAR' | 'OTHER', string> = {
  KITCHEN: 'КУХНЯ', BAR: 'БАР', OTHER: 'ПРОЧЕЕ',
};

/** Общая шапка трёх форм. */
function head(
  title: string, place: string, r: ShiftReport, width: number,
): string[] {
  const out: string[] = [];
  out.push(center(place.toUpperCase(), width));
  out.push('─'.repeat(width));
  if (r.kind === 'SHIFT') {
    out.push(padLR(title, `Смена №${r.shiftNumber ?? '—'}`, width));
    out.push(padLR(`Открыта: ${dt(r.openedAt)}`,
      r.closedAt ? `Закрыта: ${tm(r.closedAt)}` : 'открыта', width));
    if (r.openedByName) out.push(`Кассир: ${r.openedByName}`);
  } else {
    out.push(padLR(title, 'за день', width));
    out.push(padLR(`С ${dt(r.periodFrom)}`, `по ${tm(r.periodTo)}`, width));
  }
  out.push(padLR('Снят:', dt(new Date().toISOString()), width));
  out.push('─'.repeat(width));
  return out;
}

/** Денежное тело: продажи, оплаты, наличные, разрезы. Общая часть X и Z. */
function moneyBody(r: ShiftReport, width: number): string[] {
  const out: string[] = [];
  out.push(padLR('Чеков закрыто', String(r.checks), width));
  out.push(padLR('Позиций продано', String(r.itemsCount), width));
  if (r.cancelled > 0) out.push(padLR('Возвратов (отмен)', String(r.cancelled), width));
  out.push(padLR('Средний чек', N(r.avgCheck), width));
  out.push('─'.repeat(width));
  /*
   * ПРОДАЖИ — цепочка от базы к выручке. Скидки видимы ВСЕГДА,
   * даже нулевые: отчёт читают глазами каждый день, и отсутствие
   * строки выглядит вопросом, а не нулём. Рядом — сколько чеков
   * со скидкой: злоупотребления заметны одним взглядом
   */
  out.push('ПРОДАЖИ');
  const gross = r.revenue + r.discounts - r.service;
  out.push(padLR('Сумма по блюдам', N(gross), width));
  const dLabel = r.discountChecks > 0
    ? `Скидки (${r.discountChecks} ${checksWord(r.discountChecks)})`
    : 'Скидки';
  out.push(padLR(dLabel, r.discounts > 0 ? `-${N(r.discounts)}` : '0', width));
  // виды скидок — из снапшота акций: контроль «кто чем торгует»
  for (const pk of r.promoKinds) {
    out.push(padLR(`  · ${pk.name} — ${pk.checks} ${checksWord(pk.checks)}`,
      `-${N(pk.sum)}`, width));
  }
  if (r.service > 0) out.push(padLR('Сервисный сбор', `+${N(r.service)}`, width));
  out.push('═'.repeat(width));
  out.push(padLR('ВЫРУЧКА ИТОГО', N(r.revenue), width));
  out.push('─'.repeat(width));
  out.push('ОПЛАТЫ');
  const label: Record<string, string> = {
    CASH: 'Наличные', CARD: 'Карта', KASPI_QR: 'Kaspi QR',
  };
  for (const p of r.payments) out.push(padLR(label[p.method]!, N(p.amount), width));
  out.push('─'.repeat(width));
  out.push('НАЛИЧНЫЕ В ЯЩИКЕ (расчётно)');
  out.push(padLR('Размен на открытии', N(r.openingCash), width));
  if (r.cashIn > 0) out.push(padLR('+ Внесения', N(r.cashIn), width));
  if (r.cashOut > 0) out.push(padLR('- Изъятия', `-${N(r.cashOut)}`, width));
  out.push(padLR('+ Наличная выручка', N(r.cashSales), width));
  out.push(padLR('= Должно быть в ящике', N(r.expectedCash), width));
  if (r.crossDayOpenShift) {
    out.push('! Смена прошлых дней не закрыта: в ящике');
    out.push('! могут лежать и вчерашние наличные');
  }
  out.push('─'.repeat(width));
  if (r.stations.length > 0) {
    out.push('ПО ЦЕХАМ');
    for (const s of r.stations) {
      // lines — уникальных блюд; «Позиций продано» выше — строк чеков
      out.push(padLR(`${STATION_RU[s.station]} · блюд ${s.lines}`, N(s.sum), width));
    }
  }
  if (r.byWaiter.length > 0) {
    out.push('ПО ОФИЦИАНТАМ');
    for (const w of r.byWaiter) {
      out.push(padLR(`${w.name} · чеков ${w.checks}`, N(w.sum), width));
    }
  }
  if (r.voids.count > 0) {
    out.push(padLR(`Снятий: ${r.voids.count}`,
      r.voids.sum > 0 ? N(r.voids.sum) : '', width));
  }
  if (r.tips > 0) out.push(padLR('Чаевые картой', N(r.tips), width));
  if (r.hits.length > 0) {
    out.push('─'.repeat(width));
    // хиты — одной строкой и строго в ширину ленты
    const hitsLine = 'ХИТЫ: ' + r.hits.slice(0, 3)
      .map((h) => `${h.name} ×${qtyStr(h.qty)}`).join(' · ');
    out.push(hitsLine.length > width
      ? hitsLine.slice(0, width - 1) + '…' : hitsLine);
  }
  return out;
}

/** X-отчёт: промежуточный, без гашения. Печатается сколько угодно раз. */
export function xReportLines(
  r: ShiftReport, place: string, width = 48,
): string[] {
  const out = head('X-ОТЧЁТ', place, r, width);
  out.push(...moneyBody(r, width));
  out.push('─'.repeat(width));
  out.push(center('Без гашения: смена продолжается', width));
  out.push(center('ВСЕ СУММЫ В ТЕНГЕ', width));
  return out;
}

/** Z-отчёт: закрытие смены с пересчётом. Копия — с пометкой. */
export function zReportLines(
  r: ShiftReport, place: string,
  closing: { actualCash: number; copy?: boolean },
  width = 48,
): string[] {
  const out = head(`Z-ОТЧЁТ №${r.shiftNumber ?? '—'}`, place, r, width);
  if (closing.copy) {
    out.splice(2, 0, center('*** КОПИЯ ***', width));
  }
  out.push(...moneyBody(r, width));
  /*
   * ПРОДАНО ЗА СМЕНУ — плоский список: наименование и количество,
   * по алфавиту, без групповых заголовков. Z отвечает на вопрос
   * «что именно ушло и сколько», деньги уже выше в ПРОДАЖАХ;
   * подробности с категориями и цехами — дело Акта реализации
   */
  const sold = new Map<string, number>();
  for (const st of r.stations)
    for (const c of st.categories)
      for (const i of c.items)
        sold.set(i.name, (sold.get(i.name) ?? 0) + i.qty);
  if (sold.size > 0) {
    out.push('─'.repeat(width));
    out.push('ПРОДАНО ЗА СМЕНУ');
    const rows = [...sold.entries()].sort((a, b) => a[0].localeCompare(b[0], 'ru'));
    for (const [name, qty] of rows) out.push(padLR(name, qtyStr(qty), width));
    out.push(padLR('Всего позиций', String(rows.length), width));
  }
  out.push('═'.repeat(width));
  out.push('ПЕРЕСЧЁТ НАЛИЧНЫХ');
  out.push(padLR('Ожидалось', N(r.expectedCash), width));
  out.push(padLR('Фактически', N(closing.actualCash), width));
  const diff = closing.actualCash - r.expectedCash;
  out.push(padLR(
    diff === 0 ? 'Сходится' : diff > 0 ? 'Излишек' : 'Недостача',
    diff === 0 ? '0' : (diff > 0 ? '+' : '-') + N(Math.abs(diff)), width));
  out.push('─'.repeat(width));
  out.push('Кассир: ____________  Принял: ____________');
  out.push(center('Смена закрыта · счётчики погашены', width));
  out.push(center('ВСЕ СУММЫ В ТЕНГЕ', width));
  return out;
}

/** Акт реализации: расход блюд по цехам и категориям, затем снятия. */
export function actLines(
  r: ShiftReport, place: string, width = 48,
): string[] {
  const out = head('АКТ РЕАЛИЗАЦИИ', place, r, width);
  /*
   * Четыре колонки, как у зрелых систем: Цена за единицу — средняя
   * цена продажи (сумма/кол-во, модификаторы внутри). Артикул,
   * если задан товару, печатается перед именем: «1063 Fuse tea»
   */
  const allItems = r.stations.flatMap((s) => s.categories.flatMap((c) => c.items));
  const price = (i: { qty: number; sum: number }) =>
    i.qty > 0 ? Math.round(i.sum / i.qty) : 0;
  const sumW = Math.max(5, ...allItems.map((i) => N(i.sum).length), N(r.revenue).length);
  const qtyW = Math.max(3, ...allItems.map((i) => qtyStr(i.qty).length));
  const priceW = Math.max(4, ...allItems.map((i) => N(price(i)).length));
  const nameW = Math.max(8, width - priceW - qtyW - sumW - 6);
  const nm = (i: { name: string; sku?: string | null }) =>
    (i.sku ? `${i.sku} ` : '') + i.name;
  const row = (n: string, p: string, q: string, m: string) =>
    (n.length > nameW ? n.slice(0, nameW - 1) + '…' : n).padEnd(nameW)
    + '  ' + p.padStart(priceW) + '  ' + q.padStart(qtyW) + '  ' + m.padStart(sumW);
  out.push(row('Наименование', 'Цена', 'Кол', 'Сумма'));
  /*
   * Один цех — деление не несёт смысла: блок зовётся «ПРОДАЖИ»,
   * как попросил партнёр. Два и больше — «КУХНЯ» и «БАР» остаются:
   * повару своя правда, бармену своя
   */
  const single = r.stations.length === 1;
  for (const s of r.stations) {
    out.push('═'.repeat(width));
    out.push(center(single ? '— ПРОДАЖИ —' : `— ${STATION_RU[s.station]} —`, width));
    for (const c of s.categories) {
      out.push('─'.repeat(width));
      out.push(c.name);
      for (const i of c.items)
        out.push(row(nm(i), N(price(i)), qtyStr(i.qty), N(i.sum)));
      out.push(padLR(`  итого по категории`, N(c.sum), width));
    }
    out.push(padLR(single ? 'ИТОГО ПРОДАЖИ' : `ИТОГО ${STATION_RU[s.station]}`,
      N(s.sum), width));
  }
  out.push('═'.repeat(width));
  /*
   * Лестница итогов: читатель складывает колонку и сходится.
   * «По блюдам» = сумма всех строк выше; скидки и сервис —
   * явными ступенями; выручка равна X-отчёту той же смены
   */
  out.push(padLR('ИТОГО ПО БЛЮДАМ', N(r.revenue + r.discounts - r.service), width));
  out.push(padLR('Скидки', r.discounts > 0 ? `-${N(r.discounts)}` : '0', width));
  if (r.service > 0) out.push(padLR('Сервисный сбор', `+${N(r.service)}`, width));
  out.push(padLR('ВЫРУЧКА (к оплате)', N(r.revenue), width));
  if (r.voids.count > 0) {
    out.push('─'.repeat(width));
    out.push('СНЯТИЯ (со списанием)');
    for (const v of r.voids.lines) {
      const pr = v.qty && v.sum ? N(Math.round(v.sum / v.qty)) : '—';
      out.push(row(v.name, pr, v.qty === null ? '—' : qtyStr(v.qty),
        v.sum === null ? '—' : N(v.sum)));
      out.push(`  причина: ${v.reason}`);
    }
    out.push(padLR('ИТОГО СНЯТИЯ', N(r.voids.sum), width));
  }
  out.push('═'.repeat(width));
  out.push(padLR('ИТОГО ВЫРУЧКА', N(r.revenue), width));
  out.push(center('ВСЕ СУММЫ В ТЕНГЕ', width));
  return out;
}
